import React from 'react';
import { CheckIcon } from '@bizfirst/common-react';
import '../styles/variables.css';
import '../styles/ObserverConfigPage.css';
import { ObserverConfigEditor } from './ObserverConfigEditor';
import { useObserverPreferences } from '../hooks/useObserverPreferences';
import { ExecutionObserverConfigFactory } from '../models/ExecutionObserverConfig';

/**
 * ObserverConfigPage - Full page view for Observer configuration.
 * Designed to be embedded in the main app nav as a standard page.
 */
export const ObserverConfigPage: React.FC = () => {
  const { loadConfig, updateConfig, saveAsPreference } = useObserverPreferences();
  const [saved, setSaved] = React.useState(false);
  const [showSaveAs, setShowSaveAs] = React.useState(false);
  const [saveAsName, setSaveAsName] = React.useState('');
  const [config, setConfig] = React.useState(
    () => loadConfig() ?? ExecutionObserverConfigFactory.createDefault()
  );

  const handleSave = () => {
    updateConfig(config);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleReset = () => {
    const defaultConfig = ExecutionObserverConfigFactory.createDefault();
    setConfig(defaultConfig);
    updateConfig(defaultConfig);
  };

  const handleSaveAs = () => {
    if (saveAsName.trim()) {
      saveAsPreference(saveAsName.trim(), config);
      setSaveAsName('');
      setShowSaveAs(false);
    }
  };

  return (
    <div className="observer-config-page">
      {/* Page Header */}
      <div className="observer-config-page__header">
        <div className="observer-config-page__title-row">
          <div>
            <h1 className="observer-config-page__title">Observer Configuration</h1>
            <p className="observer-config-page__subtitle">
              Configure execution event filtering, entity types, and performance thresholds.
            </p>
          </div>
          <div className="observer-config-page__actions">
            <button
              className="obs-btn obs-btn--secondary"
              onClick={handleReset}
              title="Reset to default configuration"
            >
              Reset
            </button>
            <button
              className="obs-btn obs-btn--secondary"
              onClick={() => setShowSaveAs(true)}
            >
              Save As...
            </button>
            <button
              className="obs-btn obs-btn--primary"
              onClick={handleSave}
            >
              {saved ? <><CheckIcon size={14} /> Saved</> : 'Save'}
            </button>
          </div>
        </div>
      </div>

      {/* Save As Dialog */}
      {showSaveAs && (
        <div className="observer-saveas-bar">
          <span>Name:</span>
          <input
            type="text"
            value={saveAsName}
            onChange={e => setSaveAsName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleSaveAs(); if (e.key === 'Escape') setShowSaveAs(false); }}
            placeholder="e.g. Production Debug"
            autoFocus
          />
          <button className="obs-btn obs-btn--primary" onClick={handleSaveAs} disabled={!saveAsName.trim()}>Save</button>
          <button className="obs-btn obs-btn--secondary" onClick={() => setShowSaveAs(false)}>Cancel</button>
        </div>
      )}

      {/* Editor */}
      <div className="observer-config-page__body">
        <ObserverConfigEditor
          initialConfig={config}
          onConfigChange={setConfig}
          readonly={false}
        />
      </div>
    </div>
  );
};

export default ObserverConfigPage;
